import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@shared/lib/cn'
import { Button } from '@shared/ui/Button/Button'
import { PaginationSize } from './types'

export interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  /** Full pre-formatted label shown on the left, e.g. "Всего 85 участников" */
  totalLabel?: string
  size?: PaginationSize
  className?: string
}

function getPageItems(currentPage: number, totalPages: number): (number | '…')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const pages = new Set<number>([1, totalPages, currentPage])
  if (currentPage > 1) pages.add(currentPage - 1)
  if (currentPage < totalPages) pages.add(currentPage + 1)

  const sorted = Array.from(pages).sort((a, b) => a - b)
  const result: (number | '…')[] = []

  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push('…')
    }
    result.push(sorted[i])
  }

  return result
}

const arrowClass =
  'inline-flex cursor-pointer items-center justify-center rounded-[10px] transition ' +
  'hover:bg-border focus-visible:ring-2 focus-visible:ring-orange focus-visible:outline-none ' +
  'disabled:pointer-events-none disabled:opacity-40'

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  totalLabel,
  size = 'default',
  className,
}: PaginationProps) {
  const isMobile = size === PaginationSize.mobile
  const itemSize = isMobile ? 'size-7' : 'size-8'
  const gap = isMobile ? 'gap-0.5' : 'gap-2'

  const pageItems = getPageItems(currentPage, totalPages)

  return (
    <nav
      role="navigation"
      aria-label="Pagination"
      className={cn('flex items-center', totalLabel ? 'justify-between' : 'justify-end', className)}
    >
      {totalLabel && <span className="typo-base text-foreground">{totalLabel}</span>}

      <div className={cn('flex items-center', gap)}>
        <Button
          type="button"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage <= 1}
          aria-label="Previous page"
          variant="ghost"
          className={cn('h-auto p-0', arrowClass, itemSize)}
        >
          <ChevronLeft className="size-4" />
        </Button>

        {pageItems.map((item, idx) =>
          item === '…' ? (
            <span
              key={`ellipsis-${idx}`}
              className={cn(
                'text-secondary inline-flex items-center justify-center text-base',
                itemSize
              )}
            >
              …
            </span>
          ) : (
            <Button
              key={item}
              type="button"
              onClick={() => onPageChange(item)}
              aria-current={item === currentPage ? 'page' : undefined}
              variant="ghost"
              className={cn(
                'h-auto px-1.5 py-0 text-base transition',
                'focus-visible:ring-orange focus-visible:ring-2 focus-visible:outline-none',
                item === currentPage
                  ? 'border-orange bg-surface text-orange rounded-md border font-bold'
                  : 'text-foreground hover:bg-border rounded-[10px] font-normal',
                itemSize
              )}
            >
              {item}
            </Button>
          )
        )}

        <Button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage >= totalPages}
          aria-label="Next page"
          variant="ghost"
          className={cn('h-auto p-0', arrowClass, itemSize)}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </nav>
  )
}

Pagination.displayName = 'Pagination'
